import React, { useEffect, useRef, useState } from 'react';
import {
  MdVisibility,
  MdAssessment,
  MdSettings,
  MdSupportAgent,
  MdInfoOutline,
  MdClearAll,
} from 'react-icons/md';
import { Toolbar, List, Typography } from '@mui/material';
import AppBar from '@mui/material/AppBar';

import { useBackendStatusContext } from '../../context/backendStatus/backendStatusContext';
import { useTranslation } from '../../context/TranslationContext';
import { useAppConfig } from '../../context/AppConfigContext.jsx';
import logo200ImageWhite from '../../images/logo_white.png';
import SourceLink from '../SourceLink';
import { useSidebar } from '../../context/SidebarContext';
import {
  SidebarContainer,
  SidebarContent,
  SidebarBackground,
  StyledNavLink,
  SidebarListText,
  SidebarNavItemIcon,
  LogoWrapper,
  StyledIconButton,
  TABLET_MIN,
  DESKTOP_MIN,
} from '../../constants/styledComponents';

const basicNavItems = [
  { to: '/support', name: 'navItems.support', Icon: MdSupportAgent },
  { to: '/about', name: 'navItems.about', Icon: MdInfoOutline },
];

const extendedNavItems = [
  { to: '/', name: 'navItems.visualTests', Icon: MdVisibility },
  { to: '/results', name: 'navItems.results', Icon: MdAssessment },
  { to: '/settings', name: 'navItems.settings', Icon: MdSettings },
  { to: '/support', name: 'navItems.support', Icon: MdSupportAgent },
  { to: '/about', name: 'navItems.about', Icon: MdInfoOutline },
];

const getNavItems = (translate, isExtended) => {
  const items = isExtended ? extendedNavItems : basicNavItems;
  return items.map((item) => ({ ...item, name: translate(item.name) }));
};

const getMode = () => {
  const width = window.innerWidth;
  if (width >= DESKTOP_MIN) return 'desktop';
  if (width >= TABLET_MIN) return 'tablet';
  return 'mobile';
};

const Sidebar = () => {
  const backendStatus = useBackendStatusContext();
  const { translate } = useTranslation();
  const { appName } = useAppConfig();
  const [mode, setMode] = useState(getMode());
  const previousModeRef = useRef(mode);

  const [navItems, setNavItems] = useState(getNavItems(translate, false));
  const { isSidebarOpen, closeSidebar, openSidebar } = useSidebar();

  const isRail = mode === 'tablet';
  const showCollapseButton = isSidebarOpen && mode === 'mobile';

  const handleNavItemClick = () => {
    // Only the mobile overlay covers the content, so only it needs dismissing.
    if (window.innerWidth < TABLET_MIN) {
      closeSidebar();
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setMode(getMode());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const previousMode = previousModeRef.current;
    if (mode === previousMode) return;

    // Only act on band crossings so a manual toggle isn't undone mid-resize.
    if (mode === 'mobile') {
      closeSidebar();
    } else if (previousMode === 'mobile' || mode === 'desktop') {
      openSidebar();
    }
    previousModeRef.current = mode;
  }, [mode, closeSidebar, openSidebar]);

  useEffect(() => {
    const isExtended =
      backendStatus.state.status !== 'loading' ||
      backendStatus.state.isAvailable ||
      backendStatus.state.isUpdating;
    setNavItems(getNavItems(translate, isExtended));
  }, [
    backendStatus.state.isAvailable,
    backendStatus.state.isUpdating,
    backendStatus.state.status,
    translate,
  ]);

  return (
    <SidebarContainer isopen={isSidebarOpen} data-image={logo200ImageWhite}>
      <SidebarBackground />
      <SidebarContent>
        <AppBar position="static" sx={{ backgroundColor: 'transparent' }} elevation={0}>
          <Toolbar
            display="flex"
            sx={{ justifyContent: isRail ? 'center' : 'space-between', px: isRail ? 0 : undefined }}
          >
            <SourceLink style={{ display: 'flex', textDecoration: 'none' }}>
              <LogoWrapper sx={isRail ? { marginLeft: 0 } : undefined}>
                <img
                  src={logo200ImageWhite}
                  width="20"
                  height="24"
                  alt=""
                  style={{ margin: isRail ? 0 : '0px 8px 2px 6px' }}
                />
                {!isRail ? (
                  <Typography variant="h6" style={{ fontWeight: 'bold', fontSize: '22px' }}>
                    {appName}
                  </Typography>
                ) : null}
              </LogoWrapper>
            </SourceLink>

            {showCollapseButton ? (
              <StyledIconButton onClick={closeSidebar} sx={{ border: '1px solid #fff', mr: 0 }}>
                <MdClearAll size={25} style={{ color: 'white' }} />
              </StyledIconButton>
            ) : null}
          </Toolbar>
        </AppBar>
        <List>
          {navItems.map(({ to, name, Icon }, index) => (
            <StyledNavLink key={index} to={to} onClick={handleNavItemClick} rail={isRail}>
              <SidebarNavItemIcon rail={isRail}>
                <Icon style={{ color: 'white', width: '24px', height: '24px' }} />
              </SidebarNavItemIcon>
              <SidebarListText primary={name} rail={isRail} />
            </StyledNavLink>
          ))}
        </List>
      </SidebarContent>
    </SidebarContainer>
  );
};

export default Sidebar;
